import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { CheckCircle, CreditCard, Loader2, ArrowLeft, AlertCircle, Award, Calendar, Star } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Subscription = () => {
  const { user, token, isAuthenticated, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  const [promotionInfo, setPromotionInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processingSubscription, setProcessingSubscription] = useState(false);
  const [processingPromotion, setProcessingPromotion] = useState(false);
  const [companyData, setCompanyData] = useState(null);

  const isCompany = user?.user_type === 'company';
  const hasActiveSubscription = user?.subscription?.active && user?.subscription?.expires_at && new Date(user.subscription.expires_at) > new Date();

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    if (!isCompany) {
      navigate('/dashboard/individual');
      return;
    }
    fetchData();
  }, [isAuthenticated, isCompany, navigate]);

  const fetchData = async () => {
    try {
      const [subRes, promoRes, companyRes] = await Promise.all([
        fetch(`${API}/subscription/info`),
        fetch(`${API}/promotion/info`),
        fetch(`${API}/my-company`, {
          headers: { 'Authorization': `Bearer ${token}` },
          credentials: 'include',
        })
      ]);
      if (subRes.ok) setSubscriptionInfo(await subRes.json());
      if (promoRes.ok) setPromotionInfo(await promoRes.json());
      if (companyRes.ok) setCompanyData(await companyRes.json());
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async () => {
    setProcessingSubscription(true);
    try {
      const res = await fetch(`${API}/subscription/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        credentials: 'include',
        body: JSON.stringify({ origin_url: window.location.origin }),
      });
      if (res.ok) {
        const data = await res.json();
        window.location.href = data.url;
      } else {
        toast.error('Nie udało się rozpocząć płatności');
      }
    } catch (error) {
      toast.error('Wystąpił błąd');
    } finally {
      setProcessingSubscription(false);
    }
  };

  const handlePromotion = async () => {
    if (!hasActiveSubscription) {
      toast.error('Najpierw aktywuj subskrypcję, aby wykupić promowanie');
      return;
    }
    
    setProcessingPromotion(true);
    try {
      const res = await fetch(`${API}/promotion/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        credentials: 'include',
        body: JSON.stringify({ origin_url: window.location.origin }),
      });
      if (res.ok) {
        const data = await res.json();
        window.location.href = data.url;
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Nie udało się rozpocząć płatności');
      }
    } catch (error) {
      toast.error('Wystąpił błąd');
    } finally {
      setProcessingPromotion(false);
    }
  };

  const hasActivePromotion = companyData?.promotion?.active && 
    companyData?.promotion?.expires_at && 
    new Date(companyData.promotion.expires_at) > new Date();

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="subscription-page">
      <div className="bg-slate-900 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Button variant="ghost" onClick={() => navigate(-1)} className="text-slate-400 hover:text-white mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />Powrót
          </Button>
          <h1 className="font-heading font-bold text-2xl text-white">Subskrypcja i promowanie</h1>
          <p className="text-slate-400 mt-1">Zarządzaj subskrypcją i promocją firmy</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        {/* Current Status */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card className={`${hasActiveSubscription ? 'border-green-500 border-2' : 'border-orange-500 border-2'}`}>
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                {hasActiveSubscription ? <CheckCircle className="w-6 h-6 text-green-500" /> : <AlertCircle className="w-6 h-6 text-orange-500" />}
                Status subskrypcji
              </CardTitle>
            </CardHeader>
            <CardContent>
              {hasActiveSubscription ? (
                <div className="space-y-2">
                  <Badge className="bg-green-500 text-white">Aktywna</Badge>
                  <p className="text-slate-600">
                    Ważna do: <strong>{new Date(user.subscription.expires_at).toLocaleDateString('pl-PL')}</strong>
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  <Badge className="bg-orange-500 text-white">Nieaktywna</Badge>
                  <p className="text-slate-600">Aktywuj subskrypcję, aby odpowiadać na ogłoszenia.</p>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className={`${hasActivePromotion ? 'border-yellow-500 border-2' : 'border-slate-200'}`}>
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <Award className={`w-6 h-6 ${hasActivePromotion ? 'text-yellow-500' : 'text-slate-400'}`} />
                Status promowania
              </CardTitle>
            </CardHeader>
            <CardContent>
              {hasActivePromotion ? (
                <div className="space-y-2">
                  <Badge className="bg-yellow-500 text-white">Aktywne</Badge>
                  <p className="text-slate-600">
                    Ważne do: <strong>{new Date(companyData.promotion.expires_at).toLocaleDateString('pl-PL')}</strong>
                  </p>
                  <p className="text-sm text-slate-500">Twoja firma jest promowana na stronie głównej!</p>
                </div>
              ) : (
                <div className="space-y-2">
                  <Badge variant="secondary">Nieaktywne</Badge>
                  <p className="text-slate-600">Wykup promowanie, aby zwiększyć widoczność.</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Subscription Card */}
        <Card>
          <CardHeader className="text-center border-b">
            <CardTitle className="font-heading text-2xl">Subskrypcja miesięczna</CardTitle>
            <CardDescription>Pełen dostęp do platformy Giełda Budowlana</CardDescription>
          </CardHeader>
          <CardContent className="p-6">
            <div className="text-center mb-6">
              <p className="text-slate-500 text-sm">Cena netto</p>
              <p className="font-heading font-bold text-4xl text-slate-900">{subscriptionInfo?.price_net?.toFixed(2)} zł</p>
              <p className="text-slate-500 text-sm mt-1">
                + VAT {subscriptionInfo?.vat_rate?.toFixed(0)}% ({subscriptionInfo?.vat_amount?.toFixed(2)} zł)
              </p>
              <p className="font-heading font-bold text-2xl text-orange-600 mt-2">
                = {subscriptionInfo?.price_gross?.toFixed(2)} zł brutto / miesiąc
              </p>
            </div>

            <div className="mb-6 p-4 bg-slate-50 rounded-lg">
              <h4 className="font-bold mb-3">W ramach subskrypcji:</h4>
              <ul className="space-y-2">
                {subscriptionInfo?.features?.map((feature, idx) => (
                  <li key={idx} className="flex items-center gap-2 text-sm">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    {feature}
                  </li>
                ))}
              </ul>
            </div>

            {!hasActiveSubscription && (
              <Button onClick={handleSubscribe} disabled={processingSubscription} className="w-full bg-orange-500 hover:bg-orange-600 text-lg py-6" data-testid="subscribe-btn">
                {processingSubscription ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Przekierowanie...</> : <><CreditCard className="w-5 h-5 mr-2" />Aktywuj subskrypcję</>}
              </Button>
            )}
          </CardContent>
        </Card>

        {/* Promotion Card */}
        <Card className="border-2 border-yellow-200 bg-gradient-to-br from-yellow-50 to-orange-50">
          <CardHeader className="text-center border-b border-yellow-200">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Award className="w-8 h-8 text-yellow-500" />
              <Star className="w-6 h-6 text-yellow-400 fill-yellow-400" />
            </div>
            <CardTitle className="font-heading text-2xl">Promowanie firmy</CardTitle>
            <CardDescription>Wyróżnij swoją firmę na portalu</CardDescription>
          </CardHeader>
          <CardContent className="p-6">
            <div className="text-center mb-6">
              <p className="text-slate-500 text-sm">Cena netto (jednorazowo)</p>
              <p className="font-heading font-bold text-4xl text-slate-900">{promotionInfo?.price_net?.toFixed(2)} zł</p>
              <p className="text-slate-500 text-sm mt-1">
                + VAT {promotionInfo?.vat_rate?.toFixed(0)}% ({promotionInfo?.vat_amount?.toFixed(2)} zł)
              </p>
              <p className="font-heading font-bold text-2xl text-yellow-600 mt-2">
                = {promotionInfo?.price_gross?.toFixed(2)} zł brutto
              </p>
              <div className="flex items-center justify-center gap-2 mt-2 text-slate-600">
                <Calendar className="w-4 h-4" />
                <span>Czas trwania: {promotionInfo?.duration_days} dni</span>
              </div>
            </div>

            <div className="mb-6 p-4 bg-white rounded-lg border border-yellow-200">
              <h4 className="font-bold mb-3">Co zyskujesz:</h4>
              <ul className="space-y-2">
                {promotionInfo?.features?.map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-sm">
                    <CheckCircle className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" />
                    {feature}
                  </li>
                ))}
              </ul>
            </div>

            {!hasActivePromotion && (
              <>
                {!hasActiveSubscription && (
                  <div className="p-3 bg-orange-100 rounded-lg text-orange-800 text-sm mb-4 flex items-center gap-2">
                    <AlertCircle className="w-5 h-5 flex-shrink-0" />
                    <span>Aby wykupić promowanie, najpierw aktywuj subskrypcję</span>
                  </div>
                )}
                <Button 
                  onClick={handlePromotion} 
                  disabled={processingPromotion || !hasActiveSubscription} 
                  className="w-full bg-yellow-500 hover:bg-yellow-600 text-white text-lg py-6"
                  data-testid="promote-btn"
                >
                  {processingPromotion ? (
                    <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Przekierowanie...</>
                  ) : (
                    <><Award className="w-5 h-5 mr-2" />Wykup promowanie</>
                  )}
                </Button>
              </>
            )}

            {hasActivePromotion && (
              <div className="p-4 bg-green-50 rounded-lg text-green-800 text-center">
                <CheckCircle className="w-8 h-8 mx-auto mb-2 text-green-500" />
                <p className="font-bold">Promowanie aktywne!</p>
                <p className="text-sm">Twoja firma jest widoczna w sekcji "Firmy godne polecenia"</p>
              </div>
            )}

            <p className="text-xs text-slate-500 mt-4 text-center">
              Płatność obsługiwana przez Stripe. Obsługujemy BLIK, karty płatnicze i przelewy.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Subscription;
